import React, { useEffect, useState, createContext, useContext } from 'react';
import { DayKey, WeeklyHours, ClosureEvent, SpecialDay } from './BranchHoursSection';
import { PaymentMethod } from './OrdersContext';
export type BranchOrderMethods = {
  takeout: boolean;
  delivery: boolean;
  dineIn: boolean;
  orderAhead: boolean;
};
export type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export const dayKeys: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
export const dayLabels: Record<DayKey, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday'
};
export const dayShort: Record<DayKey, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun'
};
export type DayHours = {
  closed: boolean;
  open: string; // "HH:MM"
  close: string;
};
export type WeeklyHours = Record<DayKey, DayHours>;
export type MethodHoursMap = {
  dineIn: WeeklyHours | null;
  takeout: WeeklyHours | null;
  delivery: WeeklyHours | null;
  orderAhead: WeeklyHours | null;
};
export type SpecialDay = {
  id: string;
  date: string; // ISO yyyy-mm-dd
  label: string;
  closed: boolean;
  open?: string;
  close?: string;
};
export type TemporaryClosure = {
  active: boolean;
  reason: string;
  until?: string;
};
// ===== Staff & permissions =====
export type StaffRole = 'manager' | 'staff' | 'kitchen' | 'cashier';
export const staffRoles: StaffRole[] = ['manager', 'staff', 'kitchen', 'cashier'];
export const roleMeta: Record<StaffRole, {
  label: string;
  description: string;
  color: string;
}> = {
  manager: {
    label: 'Manager',
    description: 'Full access to this branch',
    color: 'amber'
  },
  staff: {
    label: 'Staff',
    description: 'Take orders and manage day-to-day',
    color: 'blue'
  },
  kitchen: {
    label: 'Kitchen',
    description: 'Sees and updates kitchen tickets',
    color: 'rose'
  },
  cashier: {
    label: 'Cashier',
    description: 'Handles payments and receipts',
    color: 'emerald'
  }
};
export type StaffPermissions = {
  viewOrders: boolean;
  manageOrders: boolean;
  viewKitchen: boolean;
  manageMenu: boolean;
  viewReports: boolean;
  manageBranchSettings: boolean;
  manageStaff: boolean;
};
export const permissionMeta: {
  key: keyof StaffPermissions;
  label: string;
  description: string;
}[] = [{
  key: 'viewOrders',
  label: 'View orders',
  description: 'See incoming and historical orders'
}, {
  key: 'manageOrders',
  label: 'Manage orders',
  description: 'Accept, refund, or cancel orders'
}, {
  key: 'viewKitchen',
  label: 'Kitchen display',
  description: 'Access the kitchen ticket queue'
}, {
  key: 'manageMenu',
  label: 'Manage menu',
  description: 'Edit items, categories and stock levels'
}, {
  key: 'viewReports',
  label: 'View reports',
  description: 'Access analytics and sales reports'
}, {
  key: 'manageBranchSettings',
  label: 'Branch settings',
  description: 'Edit hours, methods and branch info'
}, {
  key: 'manageStaff',
  label: 'Manage staff',
  description: 'Add or remove team members for this branch'
}];
export type StaffNotifications = {
  newOrderSMS: boolean;
  lowStockSMS: boolean;
  dailySummarySMS: boolean;
  cancellationSMS: boolean;
};
export const notificationMeta: {
  key: keyof StaffNotifications;
  label: string;
  description: string;
  icon: string;
}[] = [{
  key: 'newOrderSMS',
  label: 'New order',
  description: 'SMS when a customer places an order',
  icon: 'bell'
}, {
  key: 'lowStockSMS',
  label: 'Low stock',
  description: 'SMS when an item drops below its threshold',
  icon: 'alert'
}, {
  key: 'cancellationSMS',
  label: 'Cancellation',
  description: 'SMS when an order is cancelled or refunded',
  icon: 'x'
}, {
  key: 'dailySummarySMS',
  label: 'Daily summary',
  description: 'End-of-day sales recap SMS',
  icon: 'summary'
}];
export type BranchStaff = {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  role: StaffRole;
  active: boolean;
  avatarColor: string; // tailwind color name
  permissions: StaffPermissions;
  notifications: StaffNotifications;
  addedAt: number;
};
export function defaultPermissionsForRole(role: StaffRole): StaffPermissions {
  switch (role) {
    case 'manager':
      return {
        viewOrders: true,
        manageOrders: true,
        viewKitchen: true,
        manageMenu: true,
        viewReports: true,
        manageBranchSettings: true,
        manageStaff: true
      };
    case 'staff':
      return {
        viewOrders: true,
        manageOrders: true,
        viewKitchen: false,
        manageMenu: false,
        viewReports: false,
        manageBranchSettings: false,
        manageStaff: false
      };
    case 'kitchen':
      return {
        viewOrders: true,
        manageOrders: false,
        viewKitchen: true,
        manageMenu: false,
        viewReports: false,
        manageBranchSettings: false,
        manageStaff: false
      };
    case 'cashier':
      return {
        viewOrders: true,
        manageOrders: true,
        viewKitchen: false,
        manageMenu: false,
        viewReports: true,
        manageBranchSettings: false,
        manageStaff: false
      };
  }
}
export function defaultNotificationsForRole(role: StaffRole): StaffNotifications {
  switch (role) {
    case 'manager':
      return {
        newOrderSMS: true,
        lowStockSMS: true,
        cancellationSMS: true,
        dailySummarySMS: true
      };
    case 'kitchen':
      return {
        newOrderSMS: true,
        lowStockSMS: true,
        cancellationSMS: false,
        dailySummarySMS: false
      };
    case 'cashier':
      return {
        newOrderSMS: true,
        lowStockSMS: false,
        cancellationSMS: true,
        dailySummarySMS: false
      };
    case 'staff':
      return {
        newOrderSMS: true,
        lowStockSMS: false,
        cancellationSMS: false,
        dailySummarySMS: false
      };
  }
}
const avatarColors = ['orange', 'blue', 'emerald', 'purple', 'pink', 'amber', 'rose', 'indigo'];
const pickColor = (i: number) => avatarColors[i % avatarColors.length];
const makeStaff = (i: number, firstName: string, lastName: string, phone: string, role: StaffRole, overrides: Partial<BranchStaff> = {}): BranchStaff => ({
  id: `st_${firstName.toLowerCase()}_${i}_${Math.random().toString(36).slice(2, 6)}`,
  firstName,
  lastName,
  phone,
  email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@qra.et`,
  role,
  active: true,
  avatarColor: pickColor(i),
  permissions: defaultPermissionsForRole(role),
  notifications: defaultNotificationsForRole(role),
  addedAt: Date.now() - 1000 * 60 * 60 * 24 * (30 + i * 10),
  ...overrides
});
// ===== Kitchen Stations =====
// Stations are canonical across branches (so an order item with stationId='bar'
// routes correctly anywhere). Each branch picks which stations it actually runs.
export type StationId = string;
export type KitchenStation = {
  id: StationId;
  name: string;
  color: string; // tailwind color name
  icon: string; // canonical icon key
  active: boolean;
};
export const stationIcons = ['flame', 'salad', 'martini', 'cake', 'coffee', 'pizza', 'sandwich', 'utensils', 'fish', 'soup'];
export const stationColors = ['orange', 'emerald', 'blue', 'pink', 'amber', 'purple', 'rose', 'indigo'];
export const defaultStations = (): KitchenStation[] => [{
  id: 'food',
  name: 'Hot Kitchen',
  color: 'orange',
  icon: 'flame',
  active: true
}];
const fullServiceStations = (): KitchenStation[] => [{
  id: 'food',
  name: 'Hot Kitchen',
  color: 'orange',
  icon: 'flame',
  active: true
}, {
  id: 'cold',
  name: 'Cold / Salad',
  color: 'emerald',
  icon: 'salad',
  active: true
}, {
  id: 'bar',
  name: 'Bar',
  color: 'blue',
  icon: 'martini',
  active: true
}, {
  id: 'dessert',
  name: 'Dessert',
  color: 'pink',
  icon: 'cake',
  active: true
}];
const cafeStations = (): KitchenStation[] => [{
  id: 'food',
  name: 'Hot Kitchen',
  color: 'orange',
  icon: 'flame',
  active: true
}, {
  id: 'bar',
  name: 'Coffee Bar',
  color: 'amber',
  icon: 'coffee',
  active: true
}];
export type BankAccount = {
  id: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  label?: string;
  active: boolean;
};
export type Branch = {
  id: string;
  name: string;
  address: string;
  phone: string;
  active: boolean;
  kdsEnabled: boolean;
  methods: BranchOrderMethods;
  acceptedPaymentMethods: Record<PaymentMethod, boolean>;
  bankAccounts?: BankAccount[];
  manager?: string;
  createdAt: number;
  reservationsEnabled: boolean;
  hours: WeeklyHours;
  methodHours: MethodHoursMap;
  temporaryClosure: TemporaryClosure;
  specialDays: SpecialDay[];
  staff: StaffMember[];
  stations: KitchenStation[];
  settings: {
    autoAcceptOrders: boolean;
    requirePinForVoid: boolean;
    printReceiptOnClose: boolean;
    cancellationSMS: boolean;
  };
};
export const defaultHours = (open = '08:00', close = '23:00'): WeeklyHours => ({
  mon: {
    closed: false,
    open,
    close
  },
  tue: {
    closed: false,
    open,
    close
  },
  wed: {
    closed: false,
    open,
    close
  },
  thu: {
    closed: false,
    open,
    close
  },
  fri: {
    closed: false,
    open,
    close
  },
  sat: {
    closed: false,
    open,
    close
  },
  sun: {
    closed: false,
    open,
    close
  }
});
const defaultMethodHours = (): MethodHoursMap => ({
  dineIn: null,
  takeout: null,
  delivery: defaultHours('09:00', '22:00'),
  orderAhead: null
});
const defaultSpecialDays = (): SpecialDay[] => [{
  id: 's_meskel',
  date: '2026-09-27',
  label: 'Meskel',
  closed: false,
  open: '10:00',
  close: '20:00'
}, {
  id: 's_genna',
  date: '2027-01-07',
  label: 'Ethiopian Christmas (Genna)',
  closed: true
}];
const defaultClosure = (): TemporaryClosure => ({
  active: false,
  reason: ''
});
type Ctx = {
  branches: Branch[];
  branchId: string;
  setBranchId: (id: string) => void;
  menus: {
    id: string;
    name: string;
  }[];
  updateBranch: (id: string, patch: Partial<Branch>) => void;
  addBranch: (name?: string) => string;
  deleteBranch: (id: string) => void;
  addStaff: (branchId: string, partial?: Partial<BranchStaff>) => string;
  updateStaff: (branchId: string, staffId: string, patch: Partial<BranchStaff>) => void;
  removeStaff: (branchId: string, staffId: string) => void;
  addStation: (branchId: string, partial?: Partial<KitchenStation>) => string;
  updateStation: (branchId: string, stationId: string, patch: Partial<KitchenStation>) => void;
  removeStation: (branchId: string, stationId: string) => void;
};
const BranchesContext = createContext<Ctx | null>(null);
const initialMenus = [{
  id: 'm1',
  name: 'Main menu'
}, {
  id: 'm2',
  name: 'Breakfast menu'
}, {
  id: 'm3',
  name: 'Late night menu'
}, {
  id: 'm4',
  name: 'Weekend brunch'
}];
const SEED_BRANCHES: Branch[] = [{
  id: 'b_main',
  name: 'Demo Restaurant',
  address: '123 Bole Road, Addis Ababa',
  phone: '+251 911 234 567',
  active: true,
  kdsEnabled: true,
  methods: {
    dineIn: true,
    takeout: true,
    delivery: true,
    orderAhead: true
  },
  acceptedPaymentMethods: {
    card: true,
    cash: true,
    telebirr: true,
    'cbe-birr': true,
    'bank-transfer': true
  },
  bankAccounts: [{
    id: 'ba_1',
    bankName: 'Commercial Bank of Ethiopia',
    accountName: 'Demo Restaurant PLC',
    accountNumber: '1000123456789',
    label: 'CBE Main',
    active: true
  }, {
    id: 'ba_2',
    bankName: 'Awash Bank',
    accountName: 'Demo Restaurant PLC',
    accountNumber: '01320123456789',
    label: 'Awash Bole',
    active: true
  }],
  tippingOverride: {
    customerTippingEnabled: true,
    suggestedTipPcts: [10, 15, 20]
  },
  manager: 'Selam Tesfaye',
  createdAt: new Date('2024-03-12').getTime(),
  reservationsEnabled: true,
  methodHours: defaultMethodHours(),
  temporaryClosure: defaultClosure(),
  hours: {
    mon: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    tue: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    wed: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    thu: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    fri: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    sat: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    sun: {
      closed: false,
      open: '08:00',
      close: '23:00'
    }
  },
  specialDays: [],
  staff: [],
  stations: [{
    id: 'food',
    name: 'Hot Kitchen',
    color: 'orange',
    icon: 'flame',
    active: true
  }, {
    id: 'cold',
    name: 'Cold Prep',
    color: 'emerald',
    icon: 'leaf',
    active: true
  }, {
    id: 'bar',
    name: 'Bar & Drinks',
    color: 'blue',
    icon: 'coffee',
    active: true
  }, {
    id: 'dessert',
    name: 'Desserts',
    color: 'pink',
    icon: 'cake',
    active: true
  }],
  settings: {
    autoAcceptOrders: true,
    requirePinForVoid: false,
    printReceiptOnClose: true,
    cancellationSMS: true
  }
}, {
  id: 'b_express',
  name: 'Express Counter',
  address: 'Kazanchis, Addis Ababa',
  phone: '+251 922 345 678',
  active: false,
  kdsEnabled: true,
  methods: {
    dineIn: false,
    takeout: true,
    delivery: true,
    orderAhead: true
  },
  acceptedPaymentMethods: {
    card: true,
    cash: true,
    telebirr: true,
    'cbe-birr': true,
    'bank-transfer': false
  },
  manager: 'Daniel Bekele',
  createdAt: new Date('2025-01-08').getTime(),
  reservationsEnabled: false,
  methodHours: defaultMethodHours(),
  temporaryClosure: defaultClosure(),
  hours: {
    mon: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    tue: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    wed: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    thu: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    fri: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    sat: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    sun: {
      closed: false,
      open: '08:00',
      close: '23:00'
    }
  },
  specialDays: [],
  staff: [],
  stations: [{
    id: 'food',
    name: 'Express Kitchen',
    color: 'orange',
    icon: 'flame',
    active: true
  }],
  settings: {
    autoAcceptOrders: true,
    requirePinForVoid: false,
    printReceiptOnClose: true,
    cancellationSMS: true
  }
}, {
  id: 'b_mall',
  name: 'Mall Food Court',
  address: 'Century Mall, Addis Ababa',
  phone: '+251 933 456 789',
  active: false,
  kdsEnabled: true,
  methods: {
    dineIn: true,
    takeout: true,
    delivery: false,
    orderAhead: true
  },
  acceptedPaymentMethods: {
    card: true,
    cash: true,
    telebirr: true,
    'cbe-birr': false,
    'bank-transfer': false
  },
  manager: 'Hanna Girma',
  createdAt: new Date('2025-04-22').getTime(),
  reservationsEnabled: false,
  methodHours: defaultMethodHours(),
  temporaryClosure: defaultClosure(),
  hours: {
    mon: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    tue: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    wed: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    thu: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    fri: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    sat: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    sun: {
      closed: false,
      open: '08:00',
      close: '23:00'
    }
  },
  specialDays: [],
  staff: [],
  stations: [{
    id: 'food',
    name: 'Mall Kitchen',
    color: 'orange',
    icon: 'flame',
    active: true
  }],
  settings: {
    autoAcceptOrders: true,
    requirePinForVoid: false,
    printReceiptOnClose: true,
    cancellationSMS: true
  }
}, {
  id: 'b_airport',
  name: 'Airport Terminal 2',
  address: 'Bole International Airport',
  phone: '+251 944 567 890',
  active: false,
  kdsEnabled: true,
  methods: {
    dineIn: true,
    takeout: true,
    delivery: false,
    orderAhead: false
  },
  acceptedPaymentMethods: {
    card: true,
    cash: true,
    telebirr: true,
    'cbe-birr': true,
    'bank-transfer': false
  },
  manager: 'Yonas Alemu',
  createdAt: new Date('2025-08-14').getTime(),
  reservationsEnabled: false,
  methodHours: defaultMethodHours(),
  temporaryClosure: defaultClosure(),
  hours: {
    mon: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    tue: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    wed: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    thu: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    fri: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    sat: {
      closed: false,
      open: '08:00',
      close: '23:00'
    },
    sun: {
      closed: false,
      open: '08:00',
      close: '23:00'
    }
  },
  specialDays: [],
  staff: [],
  stations: [{
    id: 'food',
    name: 'Airport Kitchen',
    color: 'orange',
    icon: 'flame',
    active: true
  }],
  settings: {
    autoAcceptOrders: true,
    requirePinForVoid: false,
    printReceiptOnClose: true,
    cancellationSMS: true
  }
}];
export function isKdsEnabled(branch: Branch | undefined | null): boolean {
  return branch?.kdsEnabled !== false;
}
const initialBranches: Branch[] = SEED_BRANCHES;
// Bump the version suffix whenever the seed branches change, so a stale cached
// copy doesn't mask seed updates during development.
const BRANCHES_STORAGE_KEY = 'mp_branches_v4';
function loadInitialBranches(): Branch[] {
  if (typeof window === 'undefined') return SEED_BRANCHES;
  try {
    const raw = window.localStorage.getItem(BRANCHES_STORAGE_KEY);
    if (!raw) return SEED_BRANCHES;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    return SEED_BRANCHES;
  } catch {
    return SEED_BRANCHES;
  }
}
export function BranchesProvider({
  children
}: {
  children: ReactNode;
}) {
  const [branches, setBranches] = useState<Branch[]>(loadInitialBranches);
  const [branchId, setBranchIdState] = useState<string>(SEED_BRANCHES[0]?.id || '');
  const [menus] = useState(initialMenus);
  // Persist branch changes so the admin view and the customer-facing preview
  // (which run as separate documents) stay in sync. Writing here also fires a
  // `storage` event in other open documents.
  useEffect(() => {
    try {
      window.localStorage.setItem(BRANCHES_STORAGE_KEY, JSON.stringify(branches));
    } catch {
      /* ignore */
    }
  }, [branches]);
  // Live-sync across documents: when another document (e.g. the admin editor)
  // changes branch settings like order methods, reflect them here immediately.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== BRANCHES_STORAGE_KEY || !e.newValue) return;
      try {
        const parsed = JSON.parse(e.newValue);
        if (Array.isArray(parsed)) setBranches(parsed);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  // Re-hydrate from storage whenever this document regains focus or becomes
  // visible. `storage` events are only delivered to documents that were already
  // open and listening, so a preview that was backgrounded (or opened before an
  // admin change) can miss an update. Re-reading on focus guarantees the
  // customer view always reflects the latest branch config (e.g. dine-in being
  // turned off), so a stale copy can't allow a now-disabled order method.
  useEffect(() => {
    const rehydrate = () => {
      try {
        const raw = window.localStorage.getItem(BRANCHES_STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return;
        setBranches(prev => JSON.stringify(prev) === raw ? prev : parsed as Branch[]);
      } catch {
        /* ignore */
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') rehydrate();
    };
    window.addEventListener('focus', rehydrate);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', rehydrate);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  // Guaranteed cross-tab fallback: poll the persisted branch config so a tab
  // opened from a Direct Link reliably reflects order-method changes made in
  // another tab, even when `storage` events are missed (e.g. sandboxed preview
  // frames). Cheap JSON compare — only updates state when something changed.
  useEffect(() => {
    const id = window.setInterval(() => {
      try {
        const raw = window.localStorage.getItem(BRANCHES_STORAGE_KEY);
        if (!raw) return;
        setBranches(prev => JSON.stringify(prev) === raw ? prev : JSON.parse(raw) as Branch[]);
      } catch {
        /* ignore */
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, []);
  // If the active branch is removed, fall back to the first available one.
  const setBranchId = (id: string) => {
    if (branches.find(b => b.id === id)) {
      setBranchIdState(id);
    }
  };
  const updateBranch = (id: string, patch: Partial<Branch>) => {
    setBranches(prev => prev.map(b => b.id === id ? {
      ...b,
      ...patch
    } : b));
  };
  const addBranch = (name = 'New branch') => {
    const id = `b_${Date.now().toString(36)}`;
    const newBranch: Branch = {
      id,
      name,
      address: '',
      phone: '',
      active: false,
      kdsEnabled: true,
      methods: {
        dineIn: true,
        takeout: true,
        delivery: true,
        orderAhead: true
      },
      acceptedPaymentMethods: {
        card: true,
        cash: true,
        telebirr: true,
        'cbe-birr': true,
        'bank-transfer': false
      },
      manager: '',
      createdAt: Date.now(),
      reservationsEnabled: false,
      methodHours: defaultMethodHours(),
      temporaryClosure: defaultClosure(),
      hours: defaultHours(),
      specialDays: [],
      staff: [],
      stations: [],
      settings: {
        autoAcceptOrders: true,
        requirePinForVoid: false,
        printReceiptOnClose: true,
        cancellationSMS: false
      }
    };
    setBranches(prev => [...prev, newBranch]);
    return id;
  };
  const deleteBranch = (id: string) => {
    setBranches(prev => {
      const next = prev.filter(b => b.id !== id);
      // If the active branch is being deleted, fall back to the first remaining one
      if (branchId === id) {
        setBranchIdState(next[0]?.id || '');
      }
      return next;
    });
  };
  const addStaff = (branchId: string, partial?: Partial<BranchStaff>) => {
    const role = (partial?.role || 'staff') as StaffRole;
    const newStaff: BranchStaff = {
      id: `st_${Date.now().toString(36)}`,
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      role,
      active: true,
      avatarColor: pickColor(Math.floor(Math.random() * avatarColors.length)),
      permissions: defaultPermissionsForRole(role),
      notifications: defaultNotificationsForRole(role),
      addedAt: Date.now(),
      ...partial
    };
    setBranches(prev => prev.map(b => b.id === branchId ? {
      ...b,
      staff: [...b.staff, newStaff]
    } : b));
    return newStaff.id;
  };
  const updateStaff = (branchId: string, staffId: string, patch: Partial<BranchStaff>) => {
    setBranches(prev => prev.map(b => b.id === branchId ? {
      ...b,
      staff: b.staff.map(s => s.id === staffId ? {
        ...s,
        ...patch
      } : s)
    } : b));
  };
  const removeStaff = (branchId: string, staffId: string) => {
    setBranches(prev => prev.map(b => b.id === branchId ? {
      ...b,
      staff: b.staff.filter(s => s.id !== staffId)
    } : b));
  };
  const addStation = (branchId: string, partial?: Partial<KitchenStation>) => {
    const newId = partial?.id || `stn_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`;
    const newStation: KitchenStation = {
      id: newId,
      name: 'New station',
      color: stationColors[Math.floor(Math.random() * stationColors.length)],
      icon: 'utensils',
      active: true,
      ...partial
    };
    setBranches(prev => prev.map(b => b.id === branchId ? {
      ...b,
      stations: [...b.stations, newStation]
    } : b));
    return newId;
  };
  const updateStation = (branchId: string, stationId: string, patch: Partial<KitchenStation>) => {
    setBranches(prev => prev.map(b => b.id === branchId ? {
      ...b,
      stations: b.stations.map(s => s.id === stationId ? {
        ...s,
        ...patch
      } : s)
    } : b));
  };
  const removeStation = (branchId: string, stationId: string) => {
    setBranches(prev => prev.map(b => b.id === branchId ? {
      ...b,
      stations: b.stations.filter(s => s.id !== stationId)
    } : b));
  };
  return <BranchesContext.Provider value={{
    branches,
    branchId,
    setBranchId,
    menus,
    updateBranch,
    addBranch,
    deleteBranch,
    addStaff,
    updateStaff,
    removeStaff,
    addStation,
    updateStation,
    removeStation
  }} data-id="element-376">
      {children}
    </BranchesContext.Provider>;
}
export function useBranches() {
  const ctx = useContext(BranchesContext);
  if (!ctx) throw new Error('useBranches must be used within BranchesProvider');
  return ctx;
}
export const methodMeta: Record<keyof BranchOrderMethods, {
  label: string;
  description: string;
  color: string;
  icon: string;
}> = {
  dineIn: {
    label: 'Dine-in (Table-side QR)',
    description: 'Guests order at the table by scanning a QR code',
    color: 'purple',
    icon: 'qr'
  },
  takeout: {
    label: 'Takeout',
    description: 'Customers order and pick up their food packed for travel',
    color: 'orange',
    icon: 'bag'
  },
  delivery: {
    label: 'Delivery',
    description: "Riders deliver orders to the customer's address",
    color: 'blue',
    icon: 'bike'
  },
  orderAhead: {
    label: 'Dine-in (order ahead)',
    description: 'Customers order online and dine in on arrival',
    color: 'emerald',
    icon: 'calendar'
  }
};
export const roleColorMap: Record<string, {
  bg: string;
  text: string;
  ring: string;
}> = {
  amber: {
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    ring: 'ring-amber-200'
  },
  blue: {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    ring: 'ring-blue-200'
  },
  emerald: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200'
  },
  rose: {
    bg: 'bg-rose-100',
    text: 'text-rose-700',
    ring: 'ring-rose-200'
  }
};
export const avatarColorMap: Record<string, string> = {
  orange: 'bg-gradient-to-br from-orange-100 to-orange-200 text-orange-700',
  blue: 'bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700',
  emerald: 'bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-700',
  purple: 'bg-gradient-to-br from-purple-100 to-purple-200 text-purple-700',
  pink: 'bg-gradient-to-br from-pink-100 to-pink-200 text-pink-700',
  amber: 'bg-gradient-to-br from-amber-100 to-amber-200 text-amber-700',
  rose: 'bg-gradient-to-br from-rose-100 to-rose-200 text-rose-700',
  indigo: 'bg-gradient-to-br from-indigo-100 to-indigo-200 text-indigo-700'
};